<div class="workspace-page workspace-page--full">
  <header class="workspace-page__heading">
    <h2 class="workspace-page__title" id="dedupe-title">Review duplicate {{ tableLabel }}</h2>
    <span class="br-small br-text-muted br-ms-auto" *ngIf="review && (reviewing || scanning)" role="status">Loading…</span>
    <span class="br-small br-text-muted br-ms-auto" *ngIf="mergedTotal" role="status">{{ mergedTotal }} duplicates merged</span>
    <button type="button" class="br-btn br-btn--outline-secondary" (click)="close()" [disabled]="busy" aria-label="Back to collection"><br-icon>arrow_back</br-icon> Back</button>
  </header>

  <section class="dd-content workspace-page__panel" aria-labelledby="dedupe-title" [attr.aria-busy]="loadingColumns || scanning">
    <div class="dd-criteria br-flex br-items-center br-gap-3 br-flex-wrap">
      <span class="br-text-muted" matTooltip="Records must match in every selected field. Empty values match other empty values.">Match by</span>
      <span class="br-table-filter-rule br-table-filter-rule--removable" *ngFor="let field of chosenFields">
        <button type="button" class="br-table-filter-trigger" [brMenuTriggerFor]="fieldMenu"
                [disabled]="busy || loadingColumns" [matTooltip]="foldNote(field)">
          <span>{{ field.label }}</span><br-icon>expand_more</br-icon>
        </button>
        <button type="button" class="br-table-filter-remove" (click)="toggle(field.name)"
                [disabled]="busy || loadingColumns" [attr.aria-label]="'Remove ' + field.label"><br-icon>close</br-icon></button>
        <ng-template #fieldMenu>
          <br-menu aria-label="Field actions">
            <button brMenuItem *ngFor="let option of remainingFields" (triggered)="replaceField(field.name, option.name)" [label]="option.label"></button>
            <button brMenuItem (triggered)="toggle(field.name)" label="Remove field"></button>
          </br-menu>
        </ng-template>
      </span>
      <button type="button" class="br-table-filter-trigger" [brMenuTriggerFor]="addMenu"
              [disabled]="busy || loadingColumns || !remainingFields.length"><br-icon>add</br-icon><span>Add field</span></button>
      <ng-template #addMenu>
        <br-menu aria-label="Add field">
          <button brMenuItem *ngFor="let field of remainingFields" (triggered)="toggle(field.name)" [label]="field.label"></button>
        </br-menu>
      </ng-template>
      <button type="button" class="br-btn br-btn--primary br-ms-auto" (click)="find()"
              [disabled]="busy || scanning || loadingColumns || !hasMatchingFields">
        {{ scanning ? 'Finding...' : 'Find duplicates' }}
      </button>
    </div>

    <div class="dd-feedback" *ngIf="error || notice || (matchingFieldsChanged && scan)">
      <div *ngIf="error" class="br-alert br-alert--danger" role="alert">{{ error }}
        <button type="button" *ngIf="selected" class="br-btn br-btn--outline-secondary br-btn--sm br-ms-2"
                [disabled]="busy || reviewing || matchingFieldsChanged" (click)="loadReview()">Refresh comparison</button>
      </div>
      <p *ngIf="notice" role="status">{{ notice }}</p>
      <p *ngIf="matchingFieldsChanged && scan">Matching fields changed. Find duplicates to update results.</p>
    </div>

    <div class="dd-panes" *ngIf="scan; else scanState">
      <aside class="dd-queue" aria-label="Duplicate groups">
        <div class="dd-queue-heading br-flex br-justify-between br-gap-2 br-small br-text-muted"><span>Review queue</span><span>{{ scan.totalGroups }} groups</span></div>
        <nav class="dd-groups" aria-label="Choose a group">
          <button type="button" class="dd-group br-interactive br-rounded" *ngFor="let group of scan.groups; trackBy: groupKey"
                  [attr.aria-current]="selected?.key === group.key ? 'true' : null"
                  [disabled]="busy || scanning || matchingFieldsChanged" (click)="select(group)">
            <span class="dd-group-label"><strong>{{ recordTitle(group.records[0]) }}</strong>
              <span class="dd-group-address br-text-muted" *ngIf="recordAddress(group.records[0]) as address">{{ address }}</span>
              <span class="dd-group-meta br-flex br-justify-between br-gap-2 br-text-muted">
                <span>{{ group.recordCount }} records</span><span *ngIf="queueStatus(group) as status" [class.br-text-danger]="group.key === selected?.key && unresolvedFields.length">{{ status }}</span>
              </span>
            </span>
          </button>
        </nav>
        <div class="dd-pager br-flex br-items-center br-gap-2" *ngIf="pageCount > 1">
          <button type="button" class="br-btn br-btn--outline-secondary br-btn--sm" aria-label="Previous groups" (click)="runScan(page - 1)" [disabled]="page === 1 || busy || scanning || matchingFieldsChanged">Previous</button>
          <span>{{ page }} / {{ pageCount }}</span>
          <button type="button" class="br-btn br-btn--outline-secondary br-btn--sm" aria-label="Next groups" (click)="runScan(page + 1)" [disabled]="page >= pageCount || busy || scanning || matchingFieldsChanged">Next</button>
        </div>
      </aside>

      <section class="dd-comparison" aria-label="Compare records" [attr.aria-busy]="reviewing">
        <ng-container *ngIf="selected; else noSelection">
          <div class="dd-comparison-heading">
            <p class="br-small br-text-muted br-mb-1">{{ selected.recordCount }} records</p>
            <h3 class="br-section-title">{{ recordTitle(groupRecord) }}</h3>
            <p class="br-small br-text-muted br-mt-1 br-mb-0" *ngIf="recordAddress(groupRecord) as address">{{ address }}</p>
          </div>
          <div class="dd-comparison-toolbar br-flex br-items-center br-justify-end br-gap-3 br-flex-wrap">
            <label class="br-form-check">
              <input type="checkbox" class="br-form-check-input" [(ngModel)]="differencesOnly" />
              <span class="br-form-check-label">Differences only</span>
            </label>
          </div>
          <div class="br-alert br-alert--warning dd-warning" *ngIf="selected.sharesOnlyBlanks">All matching fields are empty. Check these records carefully.</div>
          <p class="dd-empty" *ngIf="selected.recordCount > 100">This group has {{ selected.recordCount }} records. Add matching fields to narrow it to 100 or fewer.</p>
          <p class="dd-empty" *ngIf="reviewing && !review" role="status">Loading…</p>
          <ng-container *ngIf="review">
            <div class="dd-matrix br-card br-table-responsive" tabindex="0" aria-label="Field comparison, scroll horizontally for more records">
              <table class="br-table dd-table">
                <thead><tr>
                  <th scope="col" class="dd-field">Compare fields</th>
                  <th scope="col" *ngFor="let record of review.records; let i = index; trackBy: recordKey">
                    <span class="dd-record-title">Record {{ recordLabel(i) }}</span>
                    <label class="dd-record-choice br-form-check">
                      <input type="checkbox" class="br-form-check-input" [ngModel]="record.id === keptRecordId"
                             [disabled]="!actionable" (ngModelChange)="keepRecord(record.id, $event)" [attr.aria-label]="'Keep ' + recordLabel(i) + ' (#' + record.id + ')'" />
                      <span class="br-form-check-label">Keep {{ recordLabel(i) }}</span>
                    </label>
                  </th>
                  <th scope="col" class="dd-after">
                    <span class="dd-record-title">Merge result</span>
                  </th>
                </tr></thead>
                <tbody>
                  <tr *ngFor="let field of visibleFields; trackBy: fieldKey" [class.dd-matched]="isMatchField(field.name)" [class.dd-unresolved]="isUnresolved(field.name)">
                    <th scope="row" class="dd-field" [attr.aria-label]="differs(field.name) ? field.label + ': different values' : null"><span class="dd-value">{{ field.label }}</span></th>
                    <td *ngFor="let record of review.records; let i = index; trackBy: recordKey">
                      <label class="dd-value br-form-check" *ngIf="isEditable(field.name) && differs(field.name) && record.values[field.name]?.trim(); else sourceValue">
                        <input type="radio" class="br-form-check-input" [name]="'dedupe-value-' + field.name" [attr.name]="'dedupe-value-' + field.name"
                               [ngModel]="selectedValueRecord(field.name)" [value]="record.id" [disabled]="!actionable"
                               [attr.aria-label]="'Use record ' + recordLabel(i) + ' for ' + field.label"
                               (ngModelChange)="useValue(field.name, record.id)" />
                        <span class="br-form-check-label">{{ record.values[field.name] }}</span>
                      </label>
                      <ng-template #sourceValue>
                        <span class="dd-value" [class.br-cell-empty]="!record.values[field.name]">{{ record.values[field.name] || 'Not provided' }}</span>
                      </ng-template>
                    </td>
                    <td class="dd-after">
                      <ng-container *ngIf="isEditable(field.name); else readOnlyResult">
                        <div class="dd-result-control">
                          <input type="text" class="br-form-control br-min-w-0"
                                 [id]="'dedupe-result-' + field.name" [ngModel]="resultValue(field.name)"
                                 (ngModelChange)="editResult(field.name, $event)" [disabled]="!actionable"
                                 [required]="isUnresolved(field.name)"
                                 [attr.aria-label]="field.label + ' — merge result'" [attr.aria-invalid]="isUnresolved(field.name)"
                                 [attr.aria-describedby]="isUnresolved(field.name) ? 'dedupe-error-' + field.name : null"
                                 [placeholder]="isUnresolved(field.name) ? '' : 'Not provided'" />
                          <span *ngIf="isUnresolved(field.name)" class="br-required-mark" aria-hidden="true">*</span>
                        </div>
                        <div *ngIf="isUnresolved(field.name)" class="br-small br-text-danger br-mt-1"
                             [id]="'dedupe-error-' + field.name">Choose or enter a value.</div>
                      </ng-container>
                      <ng-template #readOnlyResult>
                        <span class="dd-value" [class.br-text-muted]="review.fieldNotes[field.name]">{{ review.fieldNotes[field.name] || resultValue(field.name) || 'Not provided' }}</span>
                      </ng-template>
                    </td>
                  </tr>
                </tbody>
              </table>
              <p class="dd-empty" *ngIf="!visibleFields.length">No differing fields.</p>
            </div>
          </ng-container>
        </ng-container>
        <ng-template #noSelection><div class="dd-empty">
          {{ scan.totalGroups === 0 ? 'No duplicates found.' : 'Choose a group to review. Skipped groups stay in the queue.' }}
        </div></ng-template>
      </section>
    </div>
    <ng-template #scanState><div class="dd-empty" role="status">
      {{ loadingColumns ? 'Loading matching fields...' : scanning ? 'Finding duplicates...' : 'Choose matching fields, then find duplicates.' }}
    </div></ng-template>
  </section>

  <footer class="br-dialog-footer br-flex-wrap">
    <span *ngIf="review && unresolvedFields.length" class="br-small br-text-danger br-me-auto" role="status">
      {{ choicesNeededLabel }}
    </span>
    <button type="button" class="br-btn br-btn--outline-secondary" (click)="skip()" [disabled]="!selected || busy || reviewing || matchingFieldsChanged || scanning">Skip for now</button>
    <button type="button" class="br-btn br-btn--primary" (click)="confirmMerge()" [disabled]="!canMerge">{{ mutating ? 'Saving...' : 'Merge & next' }}</button>
  </footer>
</div>
